import React, { useState, useEffect } from 'react';
import { FiCalendar, FiPlus, FiTrash2, FiEdit2, FiCheck, FiX } from 'react-icons/fi';
import axios from 'axios';
import { API_URL } from '../config';
import { getAuthToken } from '../services/api';

const getAuthConfig = async () => {
  const token = await getAuthToken();
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const formatDateLong = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
};

function Holidays() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', name: '', is_half_day: false, notes: '' });
  const [addForm, setAddForm] = useState({ date: '', name: '', is_half_day: false, notes: '' });
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const config = await getAuthConfig();
      const res = await axios.get(`${API_URL}/holidays?year=${year}`, config);
      setHolidays(res.data || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [year]);

  const startEdit = (h) => {
    setEditingId(h.id);
    setEditForm({ date: h.date, name: h.name, is_half_day: !!h.is_half_day, notes: h.notes || '' });
  };

  const saveEdit = async () => {
    try {
      const config = await getAuthConfig();
      await axios.patch(`${API_URL}/holidays/${editingId}`, editForm, config);
      setEditingId(null);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Save failed');
    }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete "${h.name}" on ${h.date}?`)) return;
    try {
      const config = await getAuthConfig();
      await axios.delete(`${API_URL}/holidays/${h.id}`, config);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!addForm.date || !addForm.name.trim()) { alert('Date and name are required'); return; }
    try {
      const config = await getAuthConfig();
      await axios.post(`${API_URL}/holidays`, addForm, config);
      setAddForm({ date: '', name: '', is_half_day: false, notes: '' });
      setShowAdd(false);
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Add failed');
    }
  };

  const inputStyle = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' };

  return (
    <div style={{ padding: '24px', maxWidth: '1000px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FiCalendar /> Company Holidays
          </h1>
          <p style={{ color: '#6b7280', margin: '6px 0 0', fontSize: '14px' }}>
            These dates surface on the Payroll page so admins remember to include holiday pay.
            Only employees marked "Holiday pay eligible" in their team profile get paid for these.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} style={{ ...inputStyle, fontWeight: 600 }}>
            {[year - 1, year, year + 1, year + 2].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button onClick={() => setShowAdd((s) => !s)} style={{ padding: '8px 14px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FiPlus /> {showAdd ? 'Cancel' : 'Add Holiday'}
          </button>
        </div>
      </div>

      {showAdd && (
        <form onSubmit={submitAdd} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px', marginBottom: '16px', display: 'grid', gridTemplateColumns: 'auto 2fr auto auto', gap: '10px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Date</label>
            <input type="date" value={addForm.date} onChange={(e) => setAddForm(p => ({ ...p, date: e.target.value }))} style={inputStyle} required />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>Name</label>
            <input value={addForm.name} onChange={(e) => setAddForm(p => ({ ...p, name: e.target.value }))} placeholder="Memorial Day" style={{ ...inputStyle, width: '100%' }} required />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={addForm.is_half_day} onChange={(e) => setAddForm(p => ({ ...p, is_half_day: e.target.checked }))} />
            Half day
          </label>
          <button type="submit" style={{ padding: '8px 16px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}>Add</button>
        </form>
      )}

      <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>Loading...</div>
        ) : holidays.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>No holidays for {year}. Click "Add Holiday" to add one.</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px' }}>Date</th>
                <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px' }}>Type</th>
                <th style={{ padding: '12px', width: '120px' }}></th>
              </tr>
            </thead>
            <tbody>
              {holidays.map(h => {
                const isEditing = editingId === h.id;
                return (
                  <tr key={h.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontSize: '14px', color: '#374151' }}>
                      {isEditing
                        ? <input type="date" value={editForm.date} onChange={(e) => setEditForm(p => ({ ...p, date: e.target.value }))} style={inputStyle} />
                        : formatDateLong(h.date)}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '14px' }}>
                      {isEditing
                        ? <input value={editForm.name} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} style={{ ...inputStyle, width: '100%' }} />
                        : <strong>{h.name}</strong>}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {isEditing ? (
                        <label style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <input type="checkbox" checked={editForm.is_half_day} onChange={(e) => setEditForm(p => ({ ...p, is_half_day: e.target.checked }))} />
                          Half day
                        </label>
                      ) : (
                        <span style={{ fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '10px', backgroundColor: h.is_half_day ? '#fef3c7' : '#dcfce7', color: h.is_half_day ? '#92400e' : '#166534' }}>
                          {h.is_half_day ? 'HALF DAY' : 'FULL DAY'}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} title="Save" style={{ padding: '4px 8px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '4px' }}><FiCheck size={14} /></button>
                          <button onClick={() => setEditingId(null)} title="Cancel" style={{ padding: '4px 8px', background: 'white', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}><FiX size={14} /></button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(h)} title="Edit" style={{ padding: '4px 8px', background: 'white', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer', marginRight: '4px' }}><FiEdit2 size={14} /></button>
                          <button onClick={() => remove(h)} title="Delete" style={{ padding: '4px 8px', background: 'white', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer' }}><FiTrash2 size={14} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Holidays;
